import {
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  VideoCameraIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import type { SocialEvent } from './types';
import type { ClubTheme } from './themes';
import { useRsvp, useCancelRsvp } from './queries';

interface EventCardProps {
  event: SocialEvent;
  clubId: string;
  theme: ClubTheme;
  onViewDetails?: (eventId: string) => void;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function isUpcoming(dateStr: string): boolean {
  return new Date(dateStr) > new Date();
}

const STATUS_COLORS: Record<string, string> = {
  live: '#22c55e',
  scheduled: '#3b82f6',
  pending: '#f59e0b',
  completed: '#6b7280',
  cancelled: '#ef4444',
  draft: '#9ca3af',
};

const RECURRENCE_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
};

export default function EventCard({ event, clubId, theme, onViewDetails }: EventCardProps) {
  const rsvp = useRsvp(clubId, event.id);
  const cancelRsvp = useCancelRsvp(clubId, event.id);
  const upcoming = isUpcoming(event.starts_at);
  const totalAttendees = event.rsvp_counts.going + event.rsvp_counts.interested;
  const isLive = event.status === 'live';
  const hasArtifacts = event.artifacts?.notes || event.artifacts?.recording_url ||
    event.artifacts?.ai_summary || (event.artifacts?.links && event.artifacts.links.length > 0);

  const handleRsvp = (status: 'going' | 'interested') => {
    if (event.my_rsvp === status) {
      cancelRsvp.mutate();
    } else {
      rsvp.mutate(status);
    }
  };

  return (
    <article
      className="rounded-2xl p-4 transition-shadow hover:shadow-md cursor-pointer"
      style={{
        backgroundColor: theme.colors.surface,
        border: isLive
          ? `2px solid ${STATUS_COLORS.live}`
          : `1px solid ${theme.colors.muted}20`,
        opacity: event.status === 'cancelled' ? 0.5 : upcoming || isLive ? 1 : 0.7,
      }}
      onClick={() => onViewDetails?.(event.id)}
    >
      {/* Status + Live indicator */}
      <div className="flex items-center gap-2 mb-2">
        {isLive && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase animate-pulse"
            style={{ backgroundColor: `${STATUS_COLORS.live}20`, color: STATUS_COLORS.live }}>
            <SignalIcon className="h-3 w-3" /> Live Now
          </span>
        )}
        {event.status === 'pending' && (
          <span className="rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: `${STATUS_COLORS.pending}20`, color: STATUS_COLORS.pending }}>
            Pending Approval
          </span>
        )}
        {event.recurrence && (
          <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: theme.colors.muted }}>
            <ArrowPathIcon className="h-3 w-3" />
            {RECURRENCE_LABELS[event.recurrence]}
          </span>
        )}
        {event.credits_entry && Number(event.credits_entry) > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium" style={{ color: theme.colors.primary }}>
            <CurrencyDollarIcon className="h-3 w-3" />
            {event.credits_entry} credits
          </span>
        )}
        {event.livekit_room_id && (
          <VideoCameraIcon className="h-3.5 w-3.5" style={{ color: theme.colors.muted }} title="Video event" />
        )}
      </div>

      {/* Date badge + title */}
      <div className="flex gap-3">
        <div
          className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl text-xs font-bold"
          style={{ backgroundColor: `${theme.colors.primary}18`, color: theme.colors.primary }}
        >
          <span className="text-[10px] uppercase">
            {new Date(event.starts_at).toLocaleDateString(undefined, { month: 'short' })}
          </span>
          <span className="text-lg leading-none">
            {new Date(event.starts_at).getDate()}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold truncate" style={{ color: theme.colors.text }}>
            {event.title}
          </h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]" style={{ color: theme.colors.muted }}>
            <span className="inline-flex items-center gap-1">
              <ClockIcon className="h-3 w-3" />
              {formatTime(event.starts_at)}
              {event.ends_at && ` - ${formatTime(event.ends_at)}`}
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />
              {formatDate(event.starts_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      {event.description && (
        <p
          className="mt-2 text-xs leading-relaxed line-clamp-2"
          style={{ color: theme.colors.muted }}
        >
          {event.description}
        </p>
      )}

      {/* Location & capacity */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: theme.colors.muted }}>
        {event.location && (
          <span className="inline-flex items-center gap-1">
            <MapPinIcon className="h-3 w-3" />
            {event.location}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <UserGroupIcon className="h-3 w-3" />
          {event.rsvp_counts.going} going
          {event.rsvp_counts.interested > 0 && `, ${event.rsvp_counts.interested} interested`}
          {event.max_attendees && ` / ${event.max_attendees} max`}
        </span>
        {event.channel && (
          <span>{event.channel.name}</span>
        )}
      </div>

      {/* Artifacts indicator */}
      {hasArtifacts && (
        <div className="mt-2 flex items-center gap-1 text-[10px]" style={{ color: theme.colors.primary }}>
          {event.artifacts.recording_url && <VideoCameraIcon className="h-3 w-3" />}
          {event.artifacts.notes && <span>Notes</span>}
          {event.artifacts.ai_summary && <span>AI Summary</span>}
          {event.artifacts.links && event.artifacts.links.length > 0 && (
            <span>{event.artifacts.links.length} resource{event.artifacts.links.length > 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {/* RSVP buttons */}
      {(upcoming || isLive) && event.status !== 'cancelled' && event.status !== 'pending' && (
        <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => handleRsvp('going')}
            disabled={rsvp.isPending || cancelRsvp.isPending}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
            style={{
              backgroundColor: event.my_rsvp === 'going' ? theme.colors.primary : `${theme.colors.primary}18`,
              color: event.my_rsvp === 'going' ? theme.colors.bg : theme.colors.primary,
            }}
          >
            {event.my_rsvp === 'going'
              ? 'Going'
              : event.credits_entry && Number(event.credits_entry) > 0
                ? `I'm Going (${event.credits_entry} cr)`
                : "I'm Going"}
          </button>
          <button
            type="button"
            onClick={() => handleRsvp('interested')}
            disabled={rsvp.isPending || cancelRsvp.isPending}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
            style={{
              backgroundColor: event.my_rsvp === 'interested' ? theme.colors.accent : `${theme.colors.muted}15`,
              color: event.my_rsvp === 'interested' ? theme.colors.bg : theme.colors.muted,
            }}
          >
            {event.my_rsvp === 'interested' ? 'Interested' : 'Interested?'}
          </button>
        </div>
      )}

      {/* Past event / cancelled labels */}
      {event.status === 'completed' && (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.muted }}>
          Completed
          {totalAttendees > 0 && ` \u00B7 ${totalAttendees} attended`}
        </div>
      )}
      {event.status === 'cancelled' && (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: STATUS_COLORS.cancelled }}>
          Cancelled
        </div>
      )}
      {!upcoming && !isLive && event.status === 'scheduled' && (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.muted }}>
          Past event
          {totalAttendees > 0 && ` \u00B7 ${totalAttendees} attended`}
        </div>
      )}
    </article>
  );
}
